import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useProcedureListStore } from "@/frontend/store/ProcedureListStore";
import { I18n } from "@/i18n/I18n";
import { useState } from "react";
import { Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { DeleteButton } from "../buttons/DeleteButton";
import { AssignProcedureTemplateModal } from "../requirements/AssignProcedureTemplateModal";
import { ProjectProcedureInstanceAssignmentParams } from "@/backend/access/projectProcedureInstanceAssignment/types";
import { Settings2 } from "lucide-react";
import { Project, ProjectParentType } from "@/backend/models/project";
import { GeneratedProcedure } from "../procedures/GeneratedProcedure";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { ProcedureInstanceBase } from "@/backend/models/procedureInstances/procedureInstanceBase";

interface ProjectProcedureInstanceAssignmentsProps {
  project: Project;
}

export function ProjectProcedureInstanceAssignments({ project }: ProjectProcedureInstanceAssignmentsProps) {
  const assignments = useProjectStore(state => state.projectProcedureInstanceAssignments);
  const createAssignment = useProjectStore(state => state.createProjectAssignment);
  const templates = useProcedureListStore(state => state.templates);
  const userClass = useGlobalStore(state => state.userClass);
  const assignedProcedureIds = assignments.map(a => a.procedureInstanceId);

  // Configured procedures are only ever rendered underneath the template they came
  // from, so any whose template we cannot render has nowhere else to appear. That
  // happens once the template is deleted or is no longer visible to this org, and it
  // leaves real procedures on the project that cannot be opened or cleaned up. Group
  // them on their own instead of dropping them off the page.
  const templateIds = new Set(templates.map(t => t.id));
  const unlinkedProcedures = useProcedureListStore(useShallow(state => state.procedures.filter(p => p.projectId === project.id && !templateIds.has(p.procedureTemplateId ?? ''))));

  if (assignments.length === 0 && unlinkedProcedures.length === 0) {
    if(userClass?.getOrgId() !== project.propelOrgId) {
      return (
        <div className="empty-list-card-content">
          <p className="list-card-help-text">
            {I18n.get('noTemplatesAssignedToThisProject')}
          </p>
        </div>
      );
    }

    return (
      <div className="empty-list-card-content">
        <AssignProcedureTemplateModal
          assignedIds={assignedProcedureIds}
          createAssignment={(procedureId: string) => createAssignment(project.id, procedureId)}
          templates={templates.filter(t => t.latest)}
          modelLabel={I18n.get('project')}
        />
        <p className="list-card-help-text">
          {I18n.get('assignATemplateToThisProject')}
        </p>
      </div>
    );
  }

  return (
    <div className="attachment-items-container">
      {assignments.map(assignment => (
        <AssignedItem key={assignment.id} assignment={assignment} project={project} />
      ))}
      {unlinkedProcedures.length > 0 && <UnlinkedProcedures procedures={unlinkedProcedures} />}
      {userClass?.getOrgId() === project.propelOrgId && <div>
        <AssignProcedureTemplateModal
          assignedIds={assignedProcedureIds}
          createAssignment={(procedureId: string) => createAssignment(project.id, procedureId)}
          templates={templates.filter(t => t.latest)}
          modelLabel={I18n.get('project')}
        />
      </div>}
    </div>

  );
}

function UnlinkedProcedures(props: { procedures: ProcedureInstanceBase[] }) {
  const { procedures } = props;
  const deleteProcedure = useProcedureListStore(state => state.deleteProcedureItem);

  return (
    <div>
      <div className="list-card-block-title">{I18n.get('unlinkedProcedures')}</div>
      <div className='list-card-meta mt-1'>{I18n.get('unlinkedProceduresHelpText')}</div>
      <div className="d-flex flex-column gap-2 mt-2">
        {procedures.map(procedure => <GeneratedProcedure key={procedure.id} procedure={procedure}>
          <DeleteButton
            onDelete={() => deleteProcedure(procedure)}
            model={I18n.get('unlinkedProcedure')}
            title={procedure.title}
            size="small"
          />
        </GeneratedProcedure>)}
      </div>
    </div>
  );
}

function AssignedItem(props: { assignment: ProjectProcedureInstanceAssignmentParams, project: Project }) {
  const { project, assignment } = props;

  const template = useProcedureListStore(useShallow(state => state.templates.find(t => t.id === assignment.procedureInstanceId)));
  const generatedProcedures = useProcedureListStore(useShallow(state => state.procedures.filter(p => p.procedureTemplateId === assignment.procedureInstanceId)));
  const initializeProcedureFromTemplate = useProcedureListStore(state => state.initializeProcedureFromTemplate);
  const deleteAssignment = useProjectStore(state => state.deleteProjectAssignment);
  const deleteProcedure = useProcedureListStore(state => state.deleteProcedureItem);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userClass = useGlobalStore(state => state.userClass);

  if(!template) return null;

  const handleCreateProcedure = async () => {
    setLoading(true);
    try {
      const response = await initializeProcedureFromTemplate(template.id, project.id);
      if (response.status === 200 && response.procedureInstance) {
        const url = `/procedureCreate/${response.procedureInstance.id}?projectId=${project.id}`;
        void navigate(url);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start gap-2">
        <Link to={`/procedures/${project.id}/${template.id}/templateProductPage`} className="list-card-block-title">{`${template.title} (${template.majorVersion}.${template.minorVersion}.${template.patchVersion})`}</Link>
        <div className="d-flex gap-2 align-items-center" style={{ flexShrink: 0 }}>
          {project.parentType !== ProjectParentType.ORGANIZATION && <Button
            className='fusa-btn-small-with-icon'
            onClick={() => void handleCreateProcedure()}
            disabled={loading}
          >
            <Settings2 size={16} />
            {loading ? I18n.get('loading') : I18n.get('configureProcedure')}
          </Button>
          }
          {userClass?.getOrgId() === project.propelOrgId && <DeleteButton
            onDelete={() => deleteAssignment(project.id, assignment.id)}
            model={I18n.get('procedureTemplateAssignment')}
            title={template.title}
            size="small"
          />}
        </div>
      </div>
      <div className='list-card-meta mt-1'>{I18n.get('configuredProcedures')}</div>
      <div className="d-flex flex-column gap-2 mt-2">
        {generatedProcedures.map(procedure => <GeneratedProcedure key={procedure.id} procedure={procedure}>
          <DeleteButton
            onDelete={() => deleteProcedure(procedure)}
            model={I18n.get('generatedProcedure')}
            title={procedure.title}
            size="small"
          />
        </GeneratedProcedure>)}
      </div>
    </div>
  );
}
